<script setup>
import {ref} from "vue";
import httpClient from "../lib/http/index.js";
import {useForm} from "laravel-precognition-vue";
import RegistrationAccount from "./registration/RegistrationAccount.vue";
import RegistrationSummary from "./registration/RegistrationSummary.vue";
import RegistrationConfirmation from "./registration/RegistrationConfirmation.vue";
import RegistrationSubscriptionType from "./registration/RegistrationSubscriptionType.vue";
import RegistrationSubscriptionPeriod from "./registration/RegistrationSubscriptionPeriod.vue";
import RegistrationSubscriptionCourses from "./registration/RegistrationSubscriptionCourses.vue";
import RegistrationSubscriptionPeriodFormulas from "./registration/RegistrationSubscriptionPeriodFormulas.vue";
import RegistrationSubscriptionSpecialFormulas from "./registration/RegistrationSubscriptionSpecialFormulas.vue";
import {endOfWeek, format, set, startOfWeek} from "date-fns";

const {privacyLink, conditionsLink} = defineProps(['privacyLink', 'conditionsLink']);

const http = httpClient();

const history = [],
    form = ref(null),
    step = ref('type'),
    error_message = ref(null),
    subscription_type = ref(null),
    subscription_period = ref(null),
    submit_loading = ref(false),
    is_auth = ref(false),
    checkboxes = ref({
        gdpr: false,
        gcs: false,
    }),
    selected_subscriptions = ref([]),
    selected_times = ref({
        dance: 1,
        fitness: 1,
    }),
    selected_courses = ref([]),
    user = ref( {} ),
    subscribe_form = useForm('post', '/api/v1/subscriptions', {
        subscription_id: null,
        selected_courses: [],
        nbr_access_dance: 0,
        nbr_access_fitness: 0,
        gdpr: false,
        gcs: false,
    }),
    subscriptions = ref([]),
    fitness_pass = ref([]),
    dances_pass = ref([]),
    specials_pass = ref([]),
    current_date = ref( set( new Date, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 } ) ),
    start = startOfWeek( current_date.value ),
    end = endOfWeek( current_date.value ),
    courses = ref( [] ),
    max_dance_times = ref(0);

function selectSubscriptionType(value) {
    subscription_type.value = value;
    selected_times.value = {};
    selected_courses.value = [];

    selected_subscriptions.value = [];
    selected_subscriptions.value.push(
        subscriptions.value.find( item => item.subscription_type === value )
    );
}

function selectSubscriptionPeriod(value) {
    subscription_period.value = value;
    selected_times.value = {};

    selected_subscriptions.value.splice(1);
    selected_subscriptions.value.push(
        fitness_pass.value.find( item => item.subscription_period === value )
    );
}

function selectCourses(value) {
    selected_courses.value = courses.value.flat().filter( item => value.includes( item.id ) );

    const selected_subscription = selected_subscriptions.value[0];

    if ( selected_subscription && selected_subscription.subscription_type === 'dance_pass' ) {
        selected_subscriptions.value.push(
            selected_subscription.items.find( item => item.times === selected_courses.value.length )
        );
    }
}

function selectNbrTimesFitness(value) {
    selected_times.value = value;
    selected_courses.value = [];

    const child_subscription = selected_subscriptions.value[1];

    if ( child_subscription ) {
        selected_subscriptions.value.splice(2);
        selected_subscriptions.value.push(
            child_subscription.items.find( item => item.times === value.fitness )
        );
    }
}

function selectNbrTimesSpecial(value) {
    selected_times.value = value;
    selected_courses.value = [];

    const subscription = selected_subscriptions.value[0];

    if ( subscription ) {
        selected_subscriptions.value.splice(1);
        selected_subscriptions.value.push(
            specials_pass.value.find(item => item.times === value.dance + value.fitness )
        );
    }
}

function authUser(value) {
    is_auth.value = value;
    authCheck();
}

function confirmCheckboxes(value) {
    checkboxes.value = value;
}

function changeStep(value) {
    error_message.value = null;

    form.value.scrollIntoView({behavior: "smooth", block: "start", inline: "start"});

    if ( value === 'submit' ) {
        return submitForm();
    }

    if ( value === 'previous' ) {
        step.value = history.pop();
    } else {
        history.push( step.value );
        step.value = value;
    }
}

async function submitForm() {
    submit_loading.value = true;

    const last_selected_item = selected_subscriptions.value[ selected_subscriptions.value.length - 1 ];

    subscribe_form.subscription_id = last_selected_item.id;
    subscribe_form.selected_courses = selected_courses.value.map( course => course.id );
    subscribe_form.nbr_access_dance = selected_times.value.dance;
    subscribe_form.nbr_access_fitness = selected_times.value.fitness;
    subscribe_form.gdpr = checkboxes.value.gdpr;
    subscribe_form.gcs = checkboxes.value.gcs;

    try {
        const response = await subscribe_form.submit();

        if ( response.data.status ) {
            form.value.submit();
        } else {
            error_message.value = response.data.message;
            submit_loading.value = false;
        }
    } catch (e) {
        console.error( e.message );
        submit_loading.value = false;
    }
}

let auth_signal = null;
async function authCheck() {
    try {
        if ( auth_signal ) {
            auth_signal.abort('Cancel request');
        }

        let response;

        auth_signal = http.signal();

        response = await http.get('/api/v1/auth/check', {}, {
            signal: auth_signal.signal
        });

        is_auth.value = response.body.status;

        if ( is_auth.value ) {
            response = await http.get('/api/v1/auth/me', {}, {
                signal: auth_signal.signal
            });

            user.value = response.body;
        }

        auth_signal = null;

        return is_auth.value;
    } catch (e) {
        console.error( e.message );
        auth_signal = null;
    }
}

async function fetchSubscriptions() {
    const response = await http.get('/api/v1/subscriptions');

    subscriptions.value = response.body;

    for (const item of response.body) {
        if ( item.course_type === 'fitness' && item.subscription_type === 'fitness_pass' ) {
            fitness_pass.value = item.items;
        }

        if ( item.course_type === 'dance' && item.subscription_type === 'dance_pass' ) {
            dances_pass.value = item.items;
            max_dance_times.value = Math.max( ...dances_pass.value.map( o => o.times ) );
        }

        if ( item.course_type === 'both' && item.subscription_type === 'special_pass' ) {
            specials_pass.value = item.items;
        }
    }
}

let courses_signal = null;
async function fetchCourses() {
    try {
        if ( courses_signal ) {
            courses_signal.abort('Cancel request');
        }

        courses_signal = http.signal();

        const params = new URLSearchParams({
            start: format( start, 'yyyy-MM-dd'),
            end: format( end, 'yyyy-MM-dd'),
            course_type: ['dance'],
        });

        let response = await http.get('/api/v1/courses/scheduled?' + params.toString(), {}, {
            signal: courses_signal.signal
        });

        courses_signal = null;

        return courses.value = response.body;
    } catch (e) {
    }
}

(async () => {
    await fetchSubscriptions();
    await fetchCourses();
    await authCheck();
})();

</script>

<template>
    <form method="post" action="/inscription" class="flex flex-col" ref="form">
        <registration-subscription-type :value="subscription_type" :subscriptions="subscriptions"
                                        @step="changeStep" @select="selectSubscriptionType" v-if="step === 'type'"></registration-subscription-type>

        <registration-subscription-period :value="subscription_period" :pass="fitness_pass" :selected="selected_subscriptions"
                                          @step="changeStep" @select="selectSubscriptionPeriod" v-if="step === 'period'"></registration-subscription-period>

        <registration-subscription-period-formulas :value="{fitness:selected_times.fitness}" :selected="selected_subscriptions"
                                                    @step="changeStep" @change="selectNbrTimesFitness" v-if="step === 'fitness-formulas'"></registration-subscription-period-formulas>

        <registration-subscription-special-formulas :value="selected_times" :pass="specials_pass" :selected="selected_subscriptions"
                                                    @step="changeStep" @change="selectNbrTimesSpecial" v-if="step === 'special-formulas'"></registration-subscription-special-formulas>

        <registration-subscription-courses :value="selected_courses" :pass="specials_pass" :courses="courses" :special="true" :times="selected_times"
                                           @step="changeStep" @change="selectCourses" v-if="step === 'special-courses'"></registration-subscription-courses>

        <registration-subscription-courses :value="selected_courses" :pass="dances_pass" :courses="courses" :special="false" :times="{dance:max_dance_times}"
                                           @step="changeStep" @change="selectCourses" v-if="step === 'dances-courses'"></registration-subscription-courses>

        <registration-account :value="is_auth" :user="user" @change="authUser" @step="changeStep" v-if="step === 'account'">
            <registration-summary :selected="selected_subscriptions" :courses="selected_courses" :times="selected_times"></registration-summary>
        </registration-account>

        <registration-confirmation :value="checkboxes" :loading="submit_loading" :privacy-link="privacyLink" :conditions-link="conditionsLink"
                                   @change="confirmCheckboxes" @step="changeStep" v-if="step === 'confirmation'">
            <registration-summary :selected="selected_subscriptions" :courses="selected_courses" :times="selected_times"></registration-summary>
        </registration-confirmation>

        <transition>
            <div class="w-full lg:w-2/3 mt-10 mx-auto text-red-500 text-center" v-if="error_message" v-html="error_message"></div>
        </transition>
    </form>
</template>